import { useEffect, useMemo, useState } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useAttendanceLocalList } from "../../../../../services/api-tansquery/attendance-local/list";
import { useDebouncedValue } from "../../../../../hooks/useDebounce";
import { Totem } from "../../../../../@types/totem";

type Option = {
    label: string;
    value: string;
}

type Props = {
    field: ControllerRenderProps<{
        descricao?: string;
        cor_fonte?: string;
        cor_fundo?: string;
        local_atendimento?: string;
        orgao?: string;
        servicos?: string[];
        prioridades?: string[];
        logo?: File | string;
    }, "local_atendimento">
    fieldState: ControllerFieldState;
    orgao: string;
    totem?: Totem;
    style?: React.CSSProperties;
}

export default function AttendanceLocalField({ field, fieldState, orgao, totem, style }: Props) {
    const [search, setSearch] = useState<string>();
    const debouncedSearch = useDebouncedValue(search, 500);
    const { data, isLoading } = useAttendanceLocalList({
        orgao, search: debouncedSearch, page: 1, ativo: true,
    });

    const attendanceLocalList = useMemo(() => {
        return data?.results || [];
    }, [data]);

    const options = useMemo(() => {
        const merged = attendanceLocalList.map(item => ({
            label: item.nome,
            value: item.slug_unidade,
        }));

        if (
            totem?.local_atendimento
            && totem?.orgao.id === Number(orgao)
        ) {
            merged.push({
                label: totem.local_atendimento.nome,
                value: totem.local_atendimento.slug,
            });
        }

        return merged.filter(
            (item, index, self) => self.findIndex(
                i => i.value === item.value
            ) === index
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attendanceLocalList, totem, orgao]);

    function handleInputChange(_, value: string, reason: string) {
        if (reason === "input") {
            setSearch(value.trim() === "" ? undefined : value);
        }
    }

    function handleSelectChange(_, newValue: Option | null) {
        if (!newValue) {
            field.onChange("");
        } else {
            field.onChange(newValue.value);
        }

    }

    useEffect(() => {
        if (totem) {
            if (orgao === "" || totem.orgao.id !== Number(orgao)) {
                field.onChange("");
            } else {
                field.onChange(totem.local_atendimento.slug);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgao, totem]);

    return (
        <Autocomplete
            style={style}
            freeSolo={false}
            options={options}
            value={options?.find((item) => item.value === field.value) || null}
            getOptionLabel={(option) => option.label}
            getOptionSelected={(option, value) => option.value === value.value}
            onChange={handleSelectChange}
            onInputChange={handleInputChange}
            loading={isLoading}
            loadingText="Carregando locais de atendimento..."
            noOptionsText="Nenhum local de atendimento encontrado"
            renderInput={(params) => (
                <TextField
                    {...params}
                    required
                    label="Local de Atendimento"
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || "Escolha o local vinculado ao totem"}
                    InputProps={{
                        ...params.InputProps,
                        required: false,
                        endAdornment: (
                            <>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
